import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PersonaShell from './persona-shell';
import Spinner from '../../components/common/spinner';
import RuleEditorDrawer from './rule-editor';
import {
  Rule,
  RulePayload,
  ruleScope,
  useCreatePersonaRule,
  useCreateSharedRule,
  useDeleteRule,
  usePersonaRules,
  useUpdateRule,
} from '../../hooks/usePersonaAdmin';

const PersonaRulesPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const { data, isLoading } = usePersonaRules(personaId, true);

  const createPersonaRule = useCreatePersonaRule(personaId);
  const createSharedRule = useCreateSharedRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();

  const [editing, setEditing] = useState<Rule | 'new' | null>(null);

  const rules = data?.data || [];
  const personaRules = rules.filter((r) => !r.shared);
  const sharedRules = rules.filter((r) => r.shared);

  const openNew = () => setEditing('new');
  const openEdit = (rule: Rule) => setEditing(rule);
  const close = () => setEditing(null);

  const handleSubmit = async (payload: RulePayload) => {
    if (editing === 'new') {
      if (payload.chatbot_id === null) {
        await createSharedRule.mutateAsync(payload);
      } else {
        await createPersonaRule.mutateAsync(payload);
      }
    } else if (editing) {
      const updatePayload = { ...payload };
      if (updatePayload.chatbot_id === undefined) {
        updatePayload.chatbot_id = Number(personaId);
      }
      await updateRule.mutateAsync({ ruleId: editing.id, payload: updatePayload });
    }
    close();
  };

  const handleDelete = async () => {
    if (editing && editing !== 'new') {
      await deleteRule.mutateAsync(editing.id);
      close();
    }
  };

  const toggleEnabled = (rule: Rule) => {
    updateRule.mutate({ ruleId: rule.id, payload: { enabled: !rule.enabled } });
  };

  const renderRuleRow = (rule: Rule) => {
    const scope = ruleScope(rule);
    return (
      <tr key={rule.id} className="cursor-pointer border-b text-sm hover:bg-gray-50" onClick={() => openEdit(rule)}>
        <td className="py-3 pl-4 pr-2">
          <div className="font-medium text-gray-900">{rule.slug}</div>
          {rule.notes && <div className="max-w-md truncate text-xs text-gray-500">{rule.notes}</div>}
        </td>
        <td className="py-3 pr-2">
          {scope === 'shared' ? (
            <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              shared
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
              persona
            </span>
          )}
        </td>
        <td className="py-3 pr-2 text-xs text-gray-600">
          {(rule.tags || []).slice(0, 4).map((tag) => (
            <span key={tag} className="mr-1 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
              {tag}
            </span>
          ))}
        </td>
        <td className="py-3 pr-2 text-xs text-gray-600">{rule.turns?.length ?? 0}</td>
        <td className="py-3 pr-2" onClick={(e) => e.stopPropagation()}>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={rule.enabled}
              onChange={() => toggleEnabled(rule)}
            />
            <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
        </td>
      </tr>
    );
  };

  return (
    <PersonaShell personaId={personaId} tab="rules">
      <div className="mx-auto max-w-5xl space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Eval Rules</h2>
            <p className="text-xs text-gray-500">
              Rules define scenarios that eval runs will replay against this Peersona. Shared rules apply to every
              persona.{' '}
              <Link to="/personas/shared-rules" className="font-medium text-blue-600 hover:underline">
                Manage shared rules →
              </Link>
            </p>
          </div>
          <button
            className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            onClick={openNew}
          >
            New Rule
          </button>
        </div>

        {isLoading && <Spinner alignmentClass="flex items-center justify-center py-16" />}

        {!isLoading && (
          <>
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Persona rules ({personaRules.length})
              </h3>
              <div className="rounded-md border border-gray-200 bg-white shadow-sm">
                <table className="w-full table-fixed border-collapse text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <th className="py-2 pl-4">Slug / Notes</th>
                      <th className="w-24 py-2">Scope</th>
                      <th className="w-64 py-2">Tags</th>
                      <th className="w-20 py-2">Turns</th>
                      <th className="w-24 py-2">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personaRules.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-xs text-gray-400">
                          No persona-scoped rules yet.
                        </td>
                      </tr>
                    )}
                    {personaRules.map(renderRuleRow)}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Shared rules ({sharedRules.length})
              </h3>
              <div className="rounded-md border border-gray-200 bg-white shadow-sm">
                <table className="w-full table-fixed border-collapse text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <th className="py-2 pl-4">Slug / Notes</th>
                      <th className="w-24 py-2">Scope</th>
                      <th className="w-64 py-2">Tags</th>
                      <th className="w-20 py-2">Turns</th>
                      <th className="w-24 py-2">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sharedRules.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-xs text-gray-400">
                          No shared rules yet.
                        </td>
                      </tr>
                    )}
                    {sharedRules.map(renderRuleRow)}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>

      {editing && (
        <RuleEditorDrawer
          rule={editing === 'new' ? null : editing}
          onClose={close}
          onSubmit={handleSubmit}
          onDelete={editing !== 'new' ? handleDelete : undefined}
          isSubmitting={createPersonaRule.isLoading || createSharedRule.isLoading || updateRule.isLoading}
          isDeleting={deleteRule.isLoading}
        />
      )}
    </PersonaShell>
  );
};

export default PersonaRulesPage;
